import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

import { getAuthToken } from "../../api/client";
import { BobbinMobileError, bobbinMobileClient } from "./client";
import { BobbinDailyView } from "./BobbinDailyView";
import { BobbinOperationalView, type BobbinOperationalKind } from "./BobbinOperationalView";
import { BobbinSearchView } from "./BobbinSearchView";
import { formatBobbinTime, redactBobbinSecrets } from "./presentation";
import type { BobbinMobileSession } from "./types";
import "./bobbin-mobile.css";
import "./bobbin-mobile-detail.css";

type AuthenticatedSession = Extract<BobbinMobileSession, { authenticated: true }>;
type MobileTab = "daily" | "search" | BobbinOperationalKind;

const MOBILE_TABS: ReadonlyArray<{ id: MobileTab; label: string; shortLabel: string }> = [
  { id: "daily", label: "Daily", shortLabel: "Daily" },
  { id: "search", label: "Search", shortLabel: "Search" },
  { id: "activity", label: "Recent activity", shortLabel: "Recent" },
  { id: "sessions", label: "Agent sessions", shortLabel: "Sessions" },
  { id: "attention", label: "Attention", shortLabel: "Attention" },
];

function safeMessage(error: unknown): string {
  if (error instanceof BobbinMobileError) return error.message;
  if (error instanceof DOMException && error.name === "AbortError") return "";
  return "Bobbin could not complete that request.";
}

function installMobileMeta(): () => void {
  const previousTitle = document.title;
  document.title = "Bobbin — Thread";
  const definitions = [
    ["mobile-web-app-capable", "yes"],
    ["apple-mobile-web-app-capable", "yes"],
    ["apple-mobile-web-app-status-bar-style", "black-translucent"],
    ["apple-mobile-web-app-title", "Bobbin"],
  ] as const;
  const created: HTMLMetaElement[] = [];
  const restored: Array<{ element: HTMLMetaElement; content: string | null }> = [];
  definitions.forEach(([name, content]) => {
    const existing = document.head.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);
    if (existing) {
      restored.push({ element: existing, content: existing.getAttribute("content") });
      existing.content = content;
      return;
    }
    const element = document.createElement("meta");
    element.name = name;
    element.content = content;
    document.head.appendChild(element);
    created.push(element);
  });
  const manifest = document.createElement("link");
  manifest.rel = "manifest";
  manifest.href = `${import.meta.env.BASE_URL}bobbin.webmanifest`;
  manifest.dataset.bobbinMobileManifest = "true";
  document.head.appendChild(manifest);
  return () => {
    document.title = previousTitle;
    created.forEach((element) => element.remove());
    manifest.remove();
    restored.forEach(({ element, content }) => {
      if (content === null) element.removeAttribute("content");
      else element.content = content;
    });
  };
}

interface BobbinAuthenticatedAppProps {
  session: AuthenticatedSession;
  onRevoke: () => Promise<void>;
}

function BobbinAuthenticatedApp({ session, onRevoke }: BobbinAuthenticatedAppProps) {
  const [tab, setTab] = useState<MobileTab>("daily");
  const [deviceOpen, setDeviceOpen] = useState(false);
  const [revoking, setRevoking] = useState(false);
  const [revokeError, setRevokeError] = useState("");

  const revoke = async () => {
    setRevoking(true);
    setRevokeError("");
    try {
      await onRevoke();
    } catch (error) {
      setRevokeError(safeMessage(error));
      setRevoking(false);
    }
  };

  return (
    <div className="bobbin-mobile__shell">
      <header className="bobbin-mobile__topbar">
        <div className="bobbin-mobile__brand">
          <span className="bobbin-mobile__brand-mark" aria-hidden="true">B</span>
          <div>
            <strong>Bobbin</strong>
            <span>Private synced corpus</span>
          </div>
        </div>
        <button
          className="bobbin-mobile__device-button"
          type="button"
          aria-label="Manage this iPhone session"
          onClick={() => setDeviceOpen(true)}
        >
          Device
        </button>
      </header>

      <main className="bobbin-mobile__main" id="bobbin-mobile-main">
        {tab === "daily" && (
          <BobbinDailyView client={bobbinMobileClient} csrfToken={session.csrf_token} />
        )}
        {tab === "search" && (
          <BobbinSearchView client={bobbinMobileClient} csrfToken={session.csrf_token} />
        )}
        {(tab === "activity" || tab === "sessions" || tab === "attention") && (
          <BobbinOperationalView
            client={bobbinMobileClient}
            csrfToken={session.csrf_token}
            kind={tab}
          />
        )}
      </main>

      <nav className="bobbin-mobile__tabbar" aria-label="Bobbin views">
        {MOBILE_TABS.map((item) => (
          <button
            type="button"
            className={tab === item.id ? "is-active" : ""}
            aria-label={item.label}
            aria-current={tab === item.id ? "page" : undefined}
            key={item.id}
            onClick={() => setTab(item.id)}
          >
            <span aria-hidden="true">{item.id === "daily" ? "◷" : item.id === "search" ? "⌕" : "•"}</span>
            <strong>{item.shortLabel}</strong>
          </button>
        ))}
      </nav>

      {deviceOpen && (
        <div
          className="bobbin-mobile__sheet-backdrop"
          role="presentation"
          onMouseDown={(event) => {
            if (event.currentTarget === event.target) setDeviceOpen(false);
          }}
        >
          <section
            className="bobbin-mobile__sheet"
            role="dialog"
            aria-modal="true"
            aria-labelledby="bobbin-device-title"
          >
            <header>
              <div>
                <span className="bobbin-mobile__eyebrow">Enrolled device</span>
                <h2 id="bobbin-device-title">{redactBobbinSecrets(session.device.name)}</h2>
              </div>
              <button
                className="bobbin-mobile__icon-button"
                type="button"
                aria-label="Close device settings"
                onClick={() => setDeviceOpen(false)}
              >
                ×
              </button>
            </header>
            <p>
              Session expires {formatBobbinTime(session.device.expires_at)}. The credential is
              HttpOnly and is never shown to this page.
            </p>
            {revokeError && <div className="bobbin-mobile__alert" role="alert">{revokeError}</div>}
            <button
              className="bobbin-mobile__danger-button"
              type="button"
              disabled={revoking}
              onClick={() => void revoke()}
            >
              {revoking ? "Revoking…" : "Revoke this iPhone"}
            </button>
          </section>
        </div>
      )}
    </div>
  );
}

export default function BobbinMobilePage() {
  const [checked, setChecked] = useState(false);
  const [session, setSession] = useState<AuthenticatedSession | null>(null);
  const [covered, setCovered] = useState(false);
  const [unlocking, setUnlocking] = useState(false);
  const [deviceName, setDeviceName] = useState("My iPhone");
  const [enrolling, setEnrolling] = useState(false);
  const [error, setError] = useState("");
  const rootRef = useRef<HTMLDivElement | null>(null);
  const requestRef = useRef<AbortController | null>(null);

  const checkSession = useCallback(async (unlock = false) => {
    requestRef.current?.abort();
    const controller = new AbortController();
    requestRef.current = controller;
    if (unlock) setUnlocking(true);
    setError("");
    try {
      const status = await bobbinMobileClient.getSession(controller.signal);
      setSession(status.authenticated ? status : null);
      setChecked(true);
      if (unlock) setCovered(false);
    } catch (sessionError) {
      setError(safeMessage(sessionError));
    } finally {
      if (!controller.signal.aborted) setUnlocking(false);
    }
  }, []);

  useEffect(() => {
    const restoreMeta = installMobileMeta();
    void checkSession();
    return () => {
      requestRef.current?.abort();
      restoreMeta();
    };
  }, [checkSession]);

  useEffect(() => {
    const cover = () => {
      rootRef.current?.setAttribute("data-private-covered", "true");
      requestRef.current?.abort();
      setCovered(true);
      setError("");
    };
    const onVisibility = () => {
      if (document.visibilityState === "hidden") cover();
    };
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("pagehide", cover);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("pagehide", cover);
    };
  }, []);

  const enroll = async () => {
    if (!deviceName.trim()) return;
    setEnrolling(true);
    setError("");
    requestRef.current?.abort();
    const controller = new AbortController();
    requestRef.current = controller;
    try {
      const token = await getAuthToken();
      if (!token) {
        setError("Sign in to Thread in this browser before enrolling this iPhone.");
        return;
      }
      const enrolled = await bobbinMobileClient.enroll(
        deviceName.trim(),
        token,
        controller.signal,
      );
      if (!enrolled.authenticated) {
        throw new BobbinMobileError({
          status: 502,
          code: "BOBBIN_ENROLLMENT_INCOMPLETE",
          retryable: true,
        });
      }
      setSession(enrolled);
    } catch (enrollError) {
      setError(safeMessage(enrollError));
    } finally {
      if (!controller.signal.aborted) setEnrolling(false);
    }
  };

  const revoke = async () => {
    if (!session) return;
    const controller = new AbortController();
    requestRef.current = controller;
    await bobbinMobileClient.revoke(session.csrf_token, controller.signal);
    setSession(null);
    setDeviceName("My iPhone");
  };

  return (
    <div
      className="bobbin-mobile"
      data-feedback-exclude="true"
      data-private-covered={covered ? "true" : "false"}
      ref={rootRef}
    >
      {covered ? (
        <section className="bobbin-mobile__privacy-cover" aria-labelledby="bobbin-private-title">
          <span className="bobbin-mobile__brand-mark" aria-hidden="true">B</span>
          <h1 id="bobbin-private-title">Bobbin is covered</h1>
          <p>Private content was cleared when this page left the foreground.</p>
          {error && <div className="bobbin-mobile__alert" role="alert">{error}</div>}
          <button
            className="bobbin-mobile__primary-button"
            type="button"
            disabled={unlocking}
            onClick={() => void checkSession(true)}
          >
            {unlocking ? "Checking session…" : "Open Bobbin"}
          </button>
        </section>
      ) : !checked ? (
        <section className="bobbin-mobile__gate" aria-label="Checking Bobbin session">
          <span className="bobbin-mobile__brand-mark" aria-hidden="true">B</span>
          <h1>Opening Bobbin…</h1>
        </section>
      ) : session ? (
        <BobbinAuthenticatedApp session={session} onRevoke={revoke} />
      ) : (
        <section className="bobbin-mobile__gate" aria-labelledby="bobbin-enroll-title">
          <span className="bobbin-mobile__brand-mark" aria-hidden="true">B</span>
          <span className="bobbin-mobile__eyebrow">Private mobile access</span>
          <h1 id="bobbin-enroll-title">Enroll this iPhone</h1>
          <p>
            Sign in once to create a revocable, owner-only session. Bobbin never exposes the raw
            device credential to this page.
          </p>
          {error && <div className="bobbin-mobile__alert" role="alert">{error}</div>}
          <form
            onSubmit={(event) => {
              event.preventDefault();
              void enroll();
            }}
          >
            <label className="bobbin-mobile__field">
              <span>Device name</span>
              <input
                value={deviceName}
                maxLength={80}
                autoComplete="off"
                onChange={(event) => setDeviceName(event.target.value)}
              />
            </label>
            <button
              className="bobbin-mobile__primary-button"
              type="submit"
              disabled={enrolling || !deviceName.trim()}
            >
              {enrolling ? "Enrolling…" : "Enroll securely"}
            </button>
          </form>
          <Link className="bobbin-mobile__signin-link" to="/sign-in">Sign in to Thread first</Link>
          <small>
            This Bobbin web app is the direct phone experience. For agent access, add the remote
            MCP in Claude on the web, then use it in Claude Mobile. ChatGPT custom MCP apps are
            currently web-only.
          </small>
          <small>No offline cache. No service worker. Revocation takes effect immediately.</small>
        </section>
      )}
    </div>
  );
}
